// Running one closing by hand (B-40): the office's document slots with upload-and-read, a form for
// the datapoints nothing read, and "Evaluar" — no agent and no model decision anywhere in the path.
//
//   getCierreWorksheet ──► one slot per document the OFFICE profile gathers
//          │               + every datapoint a person may type in (the override's own vocabulary)
//          ├─► uploadCierreDocument ──► readCierreDocument      (two calls behind one click)
//          ├─► overrideCierreDatapoint  (value as typed, with a reason)
//          └─► evaluateCierreClosing    (evaluate now and land the state, signed by the person)
//
// Nothing here names a client or a document: the slots, the fields, their types and the sentence
// saying where each figure is printed are all the backend's, read off the office's profile and the
// client's declarations. Every refusal — 400/403/404/409 — is shown as the backend wrote it,
// through `correctionError`, because each one tells the operator what to do next.
//
// A closed closing (BALANCED, RESOLVED, CANCELLED) takes no document and no correction: the backend
// answers 409 naming the reopen. So the controls are disabled up front and the reopen is offered
// in their place — with a reason, because reopening is deliberate and visible.
//
// BR-38 — each of the four actions is also asked of the person's access on this closing's office
// (`accionesDeCierre`): one they may not take is disabled and said once at the top, in words, and
// the reopen is offered only to someone who may reopen. An Operador works a closing but does not
// reopen one, so to them a closed closing says it is closed and stops there.
import { useCallback, useEffect, useState } from "react";
import { Building2, CheckCircle, FileText, Play, RotateCcw, Upload } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Textarea,
  cn,
} from "@mantis-sas/ui";

import {
  evaluateCierreClosing,
  getCierreWorksheet,
  overrideCierreDatapoint,
  readCierreDocument,
  reopenCierreClosing,
  uploadCierreDocument,
} from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { sinPermiso } from "../../../utils/accesoDiccionario";
import { PERMISO, accionesDeCierre, objetivoDeCierre } from "../../../utils/cierrePermisos";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import {
  acceptDeFormato,
  agruparCampos,
  cambiosPendientes,
  estadoDeLectura,
  resumenDeEvaluacion,
  resumenDeLectura,
  valorVivo,
} from "../../../utils/cierreManual";
import {
  agruparPorArchivo, detallesDeCampoManual, nombreDeDato, nombreDeFuente, parteDeFuente,
} from "../../../utils/cierreNombres";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import { useClienteCierres } from "./ClienteCierresContext";
import { DetallesPlegables } from "./VariableDesplegable";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

// The panel's own inputs are `bg-card` with the theme's foreground; these match them rather than
// the primitive's hardcoded `bg-white`. (A date input's value being invisible was a different
// thing — a global rule in businessBackofficeStyles.scss; see brinksTheme.css.)
const CAMPO = "bg-card text-brinks-foreground";

const TONO = {
  ok: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  aviso: "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning",
  error: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
  neutral: "border-brinks-border bg-brinks-surface text-brinks-muted-foreground",
};

function Mensaje({ tono = "error", lineas }) {
  const texto = Array.isArray(lineas) ? lineas : [lineas];
  if (!texto.filter(Boolean).length) return null;
  return (
    <div role={tono === "error" ? "alert" : "status"} className={cn("space-y-0.5 rounded-[8px] border px-2.5 py-1.5 text-[12.5px]", TONO[tono])}>
      {texto.filter(Boolean).map((linea) => (
        <p key={linea}>{linea}</p>
      ))}
    </div>
  );
}

// On wide screens each column scrolls on its own — forty-odd datapoints must not drag the document
// slots out of view. Stacked on a narrow screen, the dialog scrolls as one page instead.
function Seccion({ titulo, children, accion, ayuda }) {
  return (
    <section {...ayuda} className="flex flex-col overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card lg:min-h-0">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
        <h3 className="text-[13.5px] font-semibold">{titulo}</h3>
        {accion}
      </header>
      <div className="space-y-2 p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">{children}</div>
    </section>
  );
}

// One file the office sends: choose it, then one click uploads it and reads it. A file that is several
// documents ("DOCUMENTOS DEL CIERRE": arqueo + notas 55 y 14, by the client's names file) is one box
// that fills them all, each read on its own. The read is a separate call, so a read that fails is
// retried with "Leer de nuevo" without sending the file again.
function SlotDocumento({ nombre, slots, closingId, token, mantisBusinessId, bloqueado, onLeido, nombres }) {
  const [archivo, setArchivo] = useState(null);
  const [paso, setPaso] = useState(null); // "subiendo" | "leyendo" | null
  const [errores, setErrores] = useState([]);
  const [resultados, setResultados] = useState([]);
  const varios = slots.length > 1;

  const leer = async () => {
    setPaso("leyendo");
    const fallos = [];
    const leidos = [];
    for (const slot of slots) {
      // eslint-disable-next-line no-await-in-loop
      const response = await readCierreDocument(token, mantisBusinessId, closingId, slot.documentKey).catch(() => null);
      const prefijo = varios ? `${parteDeFuente(nombres, slot.documentKey)}: ` : "";
      if (!response?.success) fallos.push(`${prefijo}${correctionError(response)}`);
      else leidos.push({ slot, data: response.data, prefijo });
    }
    setPaso(null);
    setErrores(fallos);
    setResultados(leidos);
    if (leidos.length) onLeido();
  };

  const subirYLeer = async () => {
    setErrores([]);
    setResultados([]);
    setPaso("subiendo");
    for (const slot of slots) {
      // eslint-disable-next-line no-await-in-loop
      const response = await uploadCierreDocument(token, mantisBusinessId, closingId, slot.documentKey, archivo).catch(() => null);
      if (!response?.success) {
        setPaso(null);
        setErrores([correctionError(response)]);
        return;
      }
    }
    setArchivo(null);
    await leer();
  };

  const { format } = slots[0];
  const unico = varios ? null : estadoDeLectura(slots[0].lastRead, slots[0].cardinality);

  return (
    <article className="space-y-2 rounded-[10px] border p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-brinks-muted-foreground" />
          <span className="truncate font-medium">{nombre}</span>
          <span className="rounded-[5px] border bg-brinks-surface px-1.5 py-0.5 text-[10.5px] font-semibold uppercase text-brinks-muted-foreground">
            {format}
          </span>
        </div>
        {unico ? <span className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium", TONO[unico.tono])}>{unico.etiqueta}</span> : null}
      </div>
      {varios ? (
        <ul className="flex flex-wrap gap-1.5">
          {slots.map((slot) => {
            const estado = estadoDeLectura(slot.lastRead, slot.cardinality);
            return (
              <li key={slot.documentKey} className={cn("rounded-full border px-2 py-0.5 text-[11px] font-medium", TONO[estado.tono])}>
                {parteDeFuente(nombres, slot.documentKey)} · {estado.etiqueta}
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="file"
          accept={acceptDeFormato(format)}
          disabled={bloqueado || !!paso}
          aria-label={`Archivo para ${nombre}`}
          onChange={(e) => setArchivo(e.target.files?.[0] || null)}
          className={`${CAMPO} min-w-0 flex-1`}
        />
        <div className="flex gap-2">
          <Button size="sm" className="gap-1.5" disabled={bloqueado || !archivo || !!paso} onClick={subirYLeer}>
            {paso ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
            {paso === "subiendo" ? "Subiendo…" : paso === "leyendo" ? "Leyendo…" : "Subir y leer"}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={bloqueado || !!paso} onClick={() => { setErrores([]); leer(); }}>
            <RotateCcw className="h-3.5 w-3.5" /> Leer de nuevo
          </Button>
        </div>
      </div>

      {!resultados.length ? slots.filter((slot) => slot.lastRead?.error).map((slot) => (
        <Mensaje
          key={slot.documentKey}
          tono="aviso"
          lineas={`Última lectura${varios ? ` de ${parteDeFuente(nombres, slot.documentKey)}` : ""}: ${slot.lastRead.error}`}
        />
      )) : null}
      <Mensaje lineas={errores} />
      {resultados.map(({ slot, data, prefijo }) => (
        <Mensaje
          key={slot.documentKey}
          tono={data.rejected ? "error" : "ok"}
          lineas={[].concat(resumenDeLectura(data, slot.cardinality, { nombres, documentKey: slot.documentKey }))
            .map((linea, i) => (i === 0 ? `${prefijo}${linea}` : linea))}
        />
      ))}
    </article>
  );
}

// The datapoints nothing read, and the ones a person needs to correct. One reason for the batch,
// sent with every changed value; each field that fails keeps its box and shows the backend's words.
function FormularioDatos({ worksheet, closingId, token, mantisBusinessId, bloqueado, onGuardado, nombres }) {
  const [borradores, setBorradores] = useState({});
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [aviso, setAviso] = useState("");
  const [editandoCampos, setEditandoCampos] = useState(() => new Set());

  const cambios = cambiosPendientes(borradores, worksheet.fields);

  const corregir = (nombre) => setEditandoCampos((actual) => new Set(actual).add(nombre));
  const cancelarCorreccion = (nombre) => {
    setEditandoCampos((actual) => {
      const siguiente = new Set(actual);
      siguiente.delete(nombre);
      return siguiente;
    });
    setBorradores((actual) => {
      const siguiente = { ...actual };
      delete siguiente[nombre];
      return siguiente;
    });
  };

  const guardar = async () => {
    setGuardando(true);
    setAviso("");
    const nuevosErrores = {};
    const guardados = [];
    for (const { field, valor } of cambios) {
      // eslint-disable-next-line no-await-in-loop -- one correction per call, each with its own author and event
      const response = await overrideCierreDatapoint(token, mantisBusinessId, closingId, field.fieldName, valor, motivo).catch(() => null);
      if (response?.success) guardados.push(field.fieldName);
      else nuevosErrores[field.fieldName] = correctionError(response);
    }
    setErrores(nuevosErrores);
    setBorradores((actual) => {
      const siguiente = { ...actual };
      guardados.forEach((nombre) => delete siguiente[nombre]);
      return siguiente;
    });
    setEditandoCampos((actual) => {
      const siguiente = new Set(actual);
      guardados.forEach((nombre) => siguiente.delete(nombre));
      return siguiente;
    });
    setGuardando(false);
    if (guardados.length) {
      setAviso(`${guardados.length} ${guardados.length === 1 ? "valor guardado" : "valores guardados"}. Evalúa el cierre para ver cómo queda.`);
      onGuardado();
    }
  };

  const grupos = agruparCampos(worksheet.fields);

  return (
    <div className="space-y-3">
      {grupos.length === 0 ? (
        <p className="text-[12.5px] text-brinks-muted-foreground">El perfil de esta oficina no declara datos que se puedan digitar.</p>
      ) : null}
      {grupos.map((grupo) => (
        <fieldset key={grupo.clave} className="space-y-1.5" disabled={bloqueado || guardando}>
          <legend className="text-[12px] font-semibold uppercase tracking-wide text-brinks-muted-foreground">
            {nombreDeFuente(nombres, grupo.sourceKey)}
            <span className="ml-1.5 font-normal normal-case">{grupo.kind === "SOURCE" ? "· llega por integración" : "· documento"}</span>
          </legend>
          {grupo.campos.map((field) => {
            const vivo = valorVivo(field.live);
            // A field that already holds a value shows it; the box appears only when the value is
            // missing or the operator asks to correct it — an empty box beside a read value reads
            // as "still to fill in".
            const editando = !vivo || editandoCampos.has(field.fieldName);
            return (
              <div key={field.fieldName} className="grid gap-1 rounded-[8px] border px-2.5 py-2 sm:grid-cols-[minmax(0,1fr)_minmax(160px,220px)] sm:items-center">
                <div className="min-w-0">
                  <Label htmlFor={`dato-${field.fieldName}`} className="text-[13px]">
                    {nombreDeDato(nombres, field.datapointKey)}
                    {field.required ? <span className="ml-0.5 text-brinks-danger" title="Obligatorio">*</span> : null}
                  </Label>
                  {/* B-47 — what it is, where it is printed and what kind of value it takes, closed
                      by default: the row stays one line, and the key and the raw type never show. */}
                  <DetallesPlegables detalles={detallesDeCampoManual({ nombres, field })} />
                  {vivo && editando ? (
                    <p className="text-[11.5px] tabular-nums text-brinks-muted-foreground">Valor actual: {vivo.valor}</p>
                  ) : null}
                  {errores[field.fieldName] ? <Mensaje lineas={errores[field.fieldName]} /> : null}
                </div>
                {editando ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      id={`dato-${field.fieldName}`}
                      className={CAMPO}
                      inputMode={field.type === "date" || field.type === "text" ? "text" : "decimal"}
                      placeholder={vivo ? "Valor corregido" : field.type === "date" ? "Digitar, como aparece en el documento" : "Digitar, ej. 450.000"}
                      value={borradores[field.fieldName] ?? ""}
                      onChange={(e) => setBorradores((actual) => ({ ...actual, [field.fieldName]: e.target.value }))}
                    />
                    {vivo ? (
                      <Button size="sm" variant="ghost" className="shrink-0 px-2 text-[12px]" onClick={() => cancelarCorreccion(field.fieldName)}>
                        Cancelar
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 rounded-[8px] border border-brinks-success-border bg-brinks-success-soft px-2.5 py-1.5">
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-semibold tabular-nums">{vivo.valor}</p>
                      <p className="truncate text-[11px] text-brinks-muted-foreground" title={vivo.por || undefined}>
                        {vivo.origen}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="shrink-0 px-2 text-[12px]" onClick={() => corregir(field.fieldName)}>
                      Corregir
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      ))}

      {worksheet.ambiguous?.length ? (
        <Mensaje
          tono="aviso"
          lineas={worksheet.ambiguous.map((a) => `«${a.fieldName}» corresponde a más de un dato (${a.candidates.join("; ")}) y no se puede digitar aquí.`)}
        />
      ) : null}

      {grupos.length ? (
        <div className="-mx-3 -mb-3 space-y-1.5 border-t bg-card px-3 pb-3 pt-3 lg:sticky lg:-bottom-3">
          <Label htmlFor="dato-motivo">Motivo</Label>
          <Textarea
            id="dato-motivo"
            className={CAMPO}
            rows={2}
            placeholder="Por qué se digita o corrige — queda registrado con tu nombre en cada valor"
            value={motivo}
            disabled={bloqueado || guardando}
            onChange={(e) => setMotivo(e.target.value)}
          />
          <Mensaje tono="ok" lineas={aviso} />
          <Button className="gap-1.5" disabled={bloqueado || guardando || cambios.length === 0 || !motivo.trim()} onClick={guardar}>
            {guardando ? <Spinner className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
            Guardar {cambios.length ? `(${cambios.length})` : ""}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Reabrir({ closingId, token, mantisBusinessId, estado, permitido, onReabierto }) {
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  const reabrir = async () => {
    setEnviando(true);
    setError("");
    const response = await reopenCierreClosing(token, mantisBusinessId, closingId, motivo).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      setError(correctionError(response));
      return;
    }
    onReabierto();
  };

  if (!permitido) {
    return (
      <div className="rounded-[12px] border border-brinks-warning-border bg-brinks-warning-soft p-3 text-[12.5px] text-brinks-warning">
        <p>Este cierre está cerrado ({estado}). No acepta documentos ni correcciones hasta que alguien lo reabra.</p>
        <p>{sinPermiso(PERMISO.REABRIR)}</p>
      </div>
    );
  }

  return (
    <div {...guia("Cierre cerrado", "Un cierre cerrado no acepta documentos ni correcciones. Para trabajarlo, reábrelo escribiendo el motivo; queda registrado.")} className="space-y-2 rounded-[12px] border border-brinks-warning-border bg-brinks-warning-soft p-3 text-[12.5px]">
      <p className="text-brinks-warning">
        Este cierre está cerrado ({estado}). No acepta documentos ni correcciones hasta que alguien lo reabra, con un
        motivo que queda registrado.
      </p>
      <Textarea className={CAMPO} rows={2} placeholder="Motivo de la reapertura" value={motivo} onChange={(e) => setMotivo(e.target.value)} disabled={enviando} />
      <Mensaje lineas={error} />
      <Button size="sm" variant="outline" className="gap-1.5" disabled={enviando || !motivo.trim()} onClick={reabrir}>
        {enviando ? <Spinner className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />} Reabrir cierre
      </Button>
    </div>
  );
}

function Contenido({ closingId, token, mantisBusinessId, aviso, region, onChanged }) {
  // B-47 — the client's readable names, shared with every other Cierres screen. B-55 — of the
  // client the shell is on, which is the one whose `panel/names` file holds them.
  const { clientCode } = useClienteCierres();
  const acceso = useAccesoCierres();
  const nombres = useNombresCierre(token, mantisBusinessId, clientCode);
  const [worksheet, setWorksheet] = useState(null);
  const [error, setError] = useState("");
  const [evaluando, setEvaluando] = useState(false);
  const [evaluacion, setEvaluacion] = useState(null);
  const [errorEvaluacion, setErrorEvaluacion] = useState("");

  const cargar = useCallback(async () => {
    const response = await getCierreWorksheet(token, mantisBusinessId, closingId).catch(() => null);
    if (!response?.success) {
      setError(correctionError(response));
      return;
    }
    setError("");
    setWorksheet(response.data);
  }, [token, mantisBusinessId, closingId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const refrescar = () => {
    cargar();
    onChanged?.();
  };

  const evaluar = async () => {
    setEvaluando(true);
    setErrorEvaluacion("");
    setEvaluacion(null);
    const response = await evaluateCierreClosing(token, mantisBusinessId, closingId).catch(() => null);
    setEvaluando(false);
    if (!response?.success) {
      setErrorEvaluacion(correctionError(response));
      return;
    }
    setEvaluacion(response.data);
    refrescar();
  };

  if (!worksheet) {
    return (
      <div className="grid min-h-[240px] place-items-center p-6">
        {error ? <Mensaje lineas={error} /> : <Spinner />}
      </div>
    );
  }

  const bloqueado = worksheet.terminal;
  // The worksheet names the closing's own client and office; the region comes from whoever opened
  // this dialog (the panel row, or the office picked in "Nuevo cierre").
  const acciones = accionesDeCierre(
    acceso,
    objetivoDeCierre({ cliente: worksheet.clientCode || clientCode, oficina: worksheet.officeCode, region })
  );
  // Said once, rather than under every disabled control. A closed closing's own notice already
  // says it takes nothing, so only what an open one would otherwise offer is listed — and nothing
  // while `/access/me` has not answered, which is not a refusal.
  const negadas = bloqueado || acceso === undefined
    ? []
    : [
        !acciones.cargar && PERMISO.CARGAR_DOCUMENTOS,
        !acciones.corregir && PERMISO.CORREGIR_VALORES,
        !acciones.evaluar && PERMISO.EVALUAR,
      ].filter(Boolean);

  return (
    <>
      <DialogHeader className="sticky top-0 z-10 shrink-0 border-b bg-card/95 p-3.5 text-left backdrop-blur">
        <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-brinks-surface">
              <Building2 className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="min-w-0 truncate text-[16px] font-semibold">
                  {worksheet.officeName || worksheet.officeCode}
                </DialogTitle>
                <BotonGuia guiaId="dialogo-trabajar-a-mano" titulo="Trabajar a mano" texto="Aquí trabajas un cierre tú mismo: subes y lees sus documentos, completas o corriges cifras y lo evalúas." nombre="Trabajar a mano" />
              </div>
              <p className="text-[12px] text-brinks-muted-foreground tabular-nums">
                {worksheet.officeCode} · {worksheet.businessDate} · {worksheet.status || worksheet.state}
              </p>
            </div>
          </div>
          <Button
            {...guia("Evaluar", "Vuelve a calcular el cuadre con los documentos y las cifras que hay ahora, y te dice qué alertas quedan abiertas.")}
            className="gap-1.5"
            disabled={bloqueado || evaluando || !acciones.evaluar}
            title={acciones.evaluar ? undefined : sinPermiso(PERMISO.EVALUAR)}
            onClick={evaluar}
          >
            {evaluando ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            Evaluar
          </Button>
        </div>
      </DialogHeader>

      <div className="flex flex-col gap-2 bg-background p-2 sm:p-3 lg:min-h-0 lg:flex-1">
        {aviso ? <Mensaje tono="aviso" lineas={aviso} /> : null}
        {negadas.length ? <Mensaje tono="neutral" lineas={negadas.map(sinPermiso)} /> : null}
        {bloqueado ? (
          <Reabrir
            closingId={closingId}
            token={token}
            mantisBusinessId={mantisBusinessId}
            estado={worksheet.status || worksheet.state}
            permitido={acciones.reabrir}
            onReabierto={refrescar}
          />
        ) : null}
        <Mensaje lineas={errorEvaluacion} />
        {evaluacion ? <Mensaje tono={evaluacion.actionableAlerts?.length ? "aviso" : "ok"} lineas={resumenDeEvaluacion(evaluacion, { nombres })} /> : null}

        <div className="grid gap-2 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <Seccion ayuda={guia("Documentos", "Un espacio por documento que reúne la oficina. Elige el archivo y toca \"Subir y leer\"; si la lectura falla, \"Leer\" lo intenta de nuevo sin volver a subirlo.")} titulo="Documentos">
            {worksheet.documents.length === 0 ? (
              <p className="text-[12.5px] text-brinks-muted-foreground">El perfil de esta oficina no pide documentos.</p>
            ) : null}
            {agruparPorArchivo(nombres, worksheet.documents.map((slot) => slot.documentKey)).map((grupo) => (
              <SlotDocumento
                key={grupo.claves.join("+")}
                nombre={grupo.nombre}
                slots={grupo.claves.map((clave) => worksheet.documents.find((slot) => slot.documentKey === clave))}
                closingId={closingId}
                token={token}
                mantisBusinessId={mantisBusinessId}
                bloqueado={bloqueado || !acciones.cargar}
                onLeido={refrescar}
                nombres={nombres}
              />
            ))}
          </Seccion>
          <Seccion ayuda={guia("Datos", "Las cifras del cierre. Un valor ya leído se muestra con \"Corregir\"; lo que falta se escribe en su casilla. Escribe el motivo y toca \"Guardar\".")} titulo="Datos">
            <FormularioDatos
              worksheet={worksheet}
              closingId={closingId}
              token={token}
              mantisBusinessId={mantisBusinessId}
              bloqueado={bloqueado || !acciones.corregir}
              onGuardado={refrescar}
              nombres={nombres}
            />
          </Seccion>
        </div>
      </div>
    </>
  );
}

// `aviso` is the opening's own warning (a day the office's calendar excludes), shown once at the
// top; `onChanged` lets the panel refresh its rows after anything here wrote. `region` is the
// office's, for a permission granted by region (BR-38) — the worksheet does not carry it.
export function CierreManualDialog({ closingId, token, mantisBusinessId, aviso, region, onOpenChange, onChanged }) {
  return (
    <Dialog open={!!closingId} onOpenChange={onOpenChange}>
      <DialogContent className="brinks-scope font-redhat flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-y-auto rounded-none border-0 p-0 sm:h-auto sm:max-h-[95vh] sm:w-[calc(100vw-24px)] sm:max-w-[1200px] sm:rounded-[18px] sm:border lg:h-[95vh] lg:overflow-hidden">
        {closingId ? (
          <Contenido
            key={closingId}
            closingId={closingId}
            token={token}
            mantisBusinessId={mantisBusinessId}
            aviso={aviso}
            region={region}
            onChanged={onChanged}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
